import { FiX, FiChevronDown, FiZap, FiEdit2, FiCheck, FiTrash2, FiPlus, FiCpu, FiPackage, FiTool, FiBriefcase, FiEdit, FiTarget, FiTrendingUp, FiSun, FiSettings, FiBarChart2, FiPenTool, FiActivity, FiStar, FiZapOff, FiMessageSquare, FiFileText, FiBook, FiAward, FiCircle, FiShare2, FiCopy, FiSave, FiDatabase } from 'react-icons/fi'
import { RAGDocument } from '../types'
import { useState, useEffect, useMemo } from 'react'
import { API_ENDPOINTS } from '../lib/api'
import { CollapsiblePanel } from './CollapsiblePanel'

interface Agent {
  id: string
  name: string
  emoji: string
  description: string
  systemPrompt: string
  temperature: number
  model: string
  color: string
  isDefault: boolean
  shareToken?: string
  shareEnabled?: boolean
  toolsPrompt?: string
  ragPrompt?: string
  createdAt: string
  updatedAt: string
}

interface PromptEditorModalProps {
  isOpen: boolean
  onClose: () => void
  prompt: string
  onChange: (value: string) => void
  ragDocuments: RAGDocument[]
  selectedRagId?: string
  onRagSelect: (id: string | null) => void
  currentLLMModel: string
  onOptimizePrompt: (prompt: string) => Promise<void>
  isOptimizing: boolean
  agents: Agent[]
  selectedAgentId: string | null
  onAgentSelect: (agentId: string | null) => void
  onAgentUpdate: (agentId: string, name: string) => Promise<void>
  onAgentUpdateFull: (agentId: string, data: Partial<Agent>) => Promise<void>
  onAgentCreate: (name: string, emoji: string) => Promise<void>
  onAgentDelete: (agentId: string) => Promise<void>
  authenticatedFetch: any
  enabledTools?: string[]
  onToolsChange?: (tools: string[]) => void
}

interface LLMConfig {
  provider: string
  model: string
  apiKey?: string
}

export function PromptEditorModal({
  isOpen,
  onClose,
  prompt,
  onChange,
  ragDocuments,
  selectedRagId,
  onRagSelect,
  currentLLMModel,
  onOptimizePrompt,
  isOptimizing,
  agents,
  selectedAgentId,
  onAgentSelect,
  onAgentUpdate,
  onAgentUpdateFull,
  onAgentCreate,
  onAgentDelete,
  authenticatedFetch,
  enabledTools = [],
  onToolsChange
}: PromptEditorModalProps) {
  // Usar enabled Tools e onToolsChange para evitar warning (implementar interface visual depois)
  void enabledTools
  void onToolsChange
  
  const [llmConfig, setLLMConfig] = useState<LLMConfig>({
    provider: 'groq',
    model: currentLLMModel
  })
  const [selectedRags, setSelectedRags] = useState<Set<string>>(new Set())
  const [isEditingAgentName, setIsEditingAgentName] = useState(false)
  const [editedAgentName, setEditedAgentName] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isCreatingAgent, setIsCreatingAgent] = useState(false)
  const [newAgentName, setNewAgentName] = useState('')
  const [newAgentEmoji, setNewAgentEmoji] = useState('CPU')
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  // Estados para compartilhamento
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [isEnablingShare, setIsEnablingShare] = useState(false)
  const [shareEnabled, setShareEnabled] = useState(false)
  const [isTogglingShare, setIsTogglingShare] = useState(false)
  
  // Estados para prompts do sistema
  const [toolsPrompt, setToolsPrompt] = useState(`Você tem acesso às seguintes ferramentas:

1. **Criação de Imagem**: Gerar imagens com IA

Use as ferramentas quando necessário para atender o usuário.`)
  
  const [isEditingToolsPrompt, setIsEditingToolsPrompt] = useState(false)
  
  // Estados para ferramentas
  const [availableTools, setAvailableTools] = useState<any[]>([])
  const [agentTools, setAgentTools] = useState<any[]>([])
  const [isLoadingTools, setIsLoadingTools] = useState(false)
  
  // Estados para painéis colapsáveis
  const [expandedPanel, setExpandedPanel] = useState<string>('tools-prompt')

  // Estados para notificação interna
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Auto-hide notification
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  // Sincronizar RAG selecionado
  useEffect(() => {
    if (selectedRagId) {
      setSelectedRags(new Set([selectedRagId]))
    }
  }, [selectedRagId])

  // Sincronizar nome do agente ao selecionar
  useEffect(() => {
    const currentAgent = agents.find(a => a.id === selectedAgentId)
    if (currentAgent) {
      setEditedAgentName(currentAgent.name)
      // Carregar estado do compartilhamento
      if (currentAgent.shareToken) {
        setShareEnabled(currentAgent.shareEnabled || false)
        const protocol = window.location.protocol
        const host = window.location.host
        setShareUrl(`${protocol}//${host}/chat/${currentAgent.shareToken}`)
      } else {
        setShareEnabled(false)
        setShareUrl(null)
      }
      // Atualizar modelo do LLM config se o agente tiver um modelo específico
      // DEBUG: Ver TUDO que chegou do backend
      console.log('[DEBUG FRONTEND] Agente completo recebido:', JSON.stringify(currentAgent, null, 2))
      console.log('[DEBUG FRONTEND] Campos específicos:', {
        hasToolsPrompt: !!currentAgent.toolsPrompt,
        hasRagPrompt: !!currentAgent.ragPrompt,
        toolsPromptType: typeof currentAgent.toolsPrompt,
        ragPromptType: typeof currentAgent.ragPrompt,
        allKeys: Object.keys(currentAgent)
      })
      
      if (currentAgent.model) {
        setLLMConfig(prev => ({
          ...prev,
          model: currentAgent.model
        }))
        console.log('[LLM] Modelo do agente carregado:', currentAgent.model)
      }
      
      // Carregar prompts do sistema se existirem
      if (currentAgent.toolsPrompt) {
        console.log('[PROMPTS] Carregando toolsPrompt do agente:', currentAgent.toolsPrompt.substring(0, 100))
        setToolsPrompt(currentAgent.toolsPrompt)
      } else {
        console.log('[PROMPTS] Agente não tem toolsPrompt customizado, usando padrão')
      }
      
      // RAG prompt existe no banco mas não tem UI de edição
      if (currentAgent.ragPrompt) {
        console.log('[PROMPTS] ragPrompt existe no banco:', currentAgent.ragPrompt.substring(0, 100))
      }
      
    }
  }, [selectedAgentId, agents])
  
  // Sincronizar prompt quando o agente muda
  useEffect(() => {
    const currentAgent = agents.find(a => a.id === selectedAgentId)
    if (currentAgent && currentAgent.systemPrompt && prompt !== currentAgent.systemPrompt) {
      onChange(currentAgent.systemPrompt)
      console.log('✅ [PROMPT EDITOR] Prompt carregado:', currentAgent.name)
    }
  }, [selectedAgentId])
  
  // Carregar ferramentas do agente
  useEffect(() => {
    const loadToolsData = async () => {
      if (!selectedAgentId) return

      setIsLoadingTools(true)
      try {
        const toolsResponse = await authenticatedFetch(API_ENDPOINTS.TOOLS)
        if (toolsResponse.ok) {
          const toolsData = await toolsResponse.json()
          setAvailableTools(toolsData.data || [])
        }
        const agentToolsResponse = await authenticatedFetch(API_ENDPOINTS.AGENT_TOOLS(selectedAgentId))
        if (agentToolsResponse.ok) {
          const agentToolsData = await agentToolsResponse.json()
          setAgentTools(agentToolsData.data || [])
        }
      } catch (error) {
        console.error('Erro ao carregar ferramentas:', error)
      } finally {
        setIsLoadingTools(false)
      }
    }
    
    loadToolsData()
  }, [selectedAgentId])

  // Lista de ícones React Icons para agentes
  const iconOptions = [
    { icon: <FiCpu className="w-5 h-5" />, name: 'CPU' },
    { icon: <FiPackage className="w-5 h-5" />, name: 'Package' },
    { icon: <FiTool className="w-5 h-5" />, name: 'Tool' },
    { icon: <FiBriefcase className="w-5 h-5" />, name: 'Briefcase' },
    { icon: <FiEdit className="w-5 h-5" />, name: 'Edit' },
    { icon: <FiTarget className="w-5 h-5" />, name: 'Target' },
    { icon: <FiTrendingUp className="w-5 h-5" />, name: 'TrendingUp' },
    { icon: <FiSun className="w-5 h-5" />, name: 'Sun' },
    { icon: <FiSettings className="w-5 h-5" />, name: 'Settings' },
    { icon: <FiBarChart2 className="w-5 h-5" />, name: 'BarChart' },
    { icon: <FiPenTool className="w-5 h-5" />, name: 'PenTool' },
    { icon: <FiActivity className="w-5 h-5" />, name: 'Activity' },
    { icon: <FiStar className="w-5 h-5" />, name: 'Star' },
    { icon: <FiZap className="w-5 h-5" />, name: 'Zap' },
    { icon: <FiZapOff className="w-5 h-5" />, name: 'Fire' },
    { icon: <FiMessageSquare className="w-5 h-5" />, name: 'Message' },
    { icon: <FiFileText className="w-5 h-5" />, name: 'FileText' },
    { icon: <FiBook className="w-5 h-5" />, name: 'Book' },
    { icon: <FiAward className="w-5 h-5" />, name: 'Award' },
    { icon: <FiCircle className="w-5 h-5" />, name: 'Circle' }
  ]

  // Função para renderizar ícone do agente
  const renderAgentIcon = (iconName: string) => {
    const iconOption = iconOptions.find(opt => opt.name === iconName)
    if (iconOption) {
      return <div className="text-primary">{iconOption.icon}</div>
    }
    // Fallback para ícone padrão
    return <FiCpu className="w-5 h-5 text-primary" />
  }

  // Obter agente atual
  const currentAgent = agents.find(a => a.id === selectedAgentId)

  // Handler para salvar nome do agente
  const handleSaveAgentName = async () => {
    if (!selectedAgentId || !editedAgentName.trim()) return
    
    await onAgentUpdate(selectedAgentId, editedAgentName.trim())
    setIsEditingAgentName(false)
  }

  // Handler para criar novo agente
  const handleCreateAgent = async () => {
    if (!newAgentName.trim()) return
    
    await onAgentCreate(newAgentName.trim(), newAgentEmoji)
    setNewAgentName('')
    setNewAgentEmoji('CPU')
    setIsCreatingAgent(false)
    setIsDropdownOpen(false)
  }

  // Handler para atualizar emoji do agente
  const handleUpdateEmoji = async (emoji: string) => {
    if (!selectedAgentId) return
    
    await onAgentUpdateFull(selectedAgentId, { emoji })
    setIsEmojiPickerOpen(false)
  }

  // Handler para deletar agente
  const handleDeleteAgent = async () => {
    if (!selectedAgentId) return
    
    await onAgentDelete(selectedAgentId)
    setShowDeleteConfirm(false)
  }

  // Handler para habilitar compartilhamento
  const handleEnableSharing = async () => {
    if (!selectedAgentId) return
    
    setIsEnablingShare(true)
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.AGENT_SHARE_ENABLE(selectedAgentId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allowFileUpload: false,
          showBranding: true,
          customWelcome: null
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        setShareUrl(data.shareUrl)
        setShareEnabled(true)
        setShowShareModal(true)
        console.log('✅ Compartilhamento habilitado:', data.shareUrl)
      }
    } catch (error) {
      console.error('❌ Erro ao habilitar compartilhamento:', error)
    } finally {
      setIsEnablingShare(false)
    }
  }

  // Handler para ativar/desativar compartilhamento
  const handleToggleSharing = async () => {
    if (!selectedAgentId) return
    
    setIsTogglingShare(true)
    try {
      if (shareEnabled) {
        // Desativar
        const response = await authenticatedFetch(API_ENDPOINTS.AGENT_SHARE_DISABLE(selectedAgentId), {
          method: 'POST'
        })
        
        if (response.ok) {
          setShareEnabled(false)
          console.log('✅ Compartilhamento desativado')
        }
      } else {
        // Ativar (ou criar se não existe)
        if (shareUrl) {
          // Já tem link, só ativar
          const response = await authenticatedFetch(API_ENDPOINTS.AGENT_SHARE_ENABLE(selectedAgentId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              allowFileUpload: false,
              showBranding: true,
              customWelcome: null
            })
          })
          
          if (response.ok) {
            setShareEnabled(true)
            console.log('✅ Compartilhamento ativado')
          }
        } else {
          // Criar novo link
          await handleEnableSharing()
        }
      }
    } catch (error) {
      console.error('❌ Erro ao alternar compartilhamento:', error)
    } finally {
      setIsTogglingShare(false)
    }
  }

  // Handler para copiar URL
  const handleCopyUrl = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl)
      console.log('📋 URL copiada!')
    }
  }

  // Handler para selecionar agente
  const handleSelectAgent = (agentId: string) => {
    onAgentSelect(agentId)
    setIsDropdownOpen(false)
  }

  const handleRagToggle = (ragId: string) => {
    const newSelected = new Set(selectedRags)
    if (newSelected.has(ragId)) {
      newSelected.delete(ragId)
      if (selectedRagId === ragId) {
        onRagSelect(null)
      }
    } else {
      newSelected.add(ragId)
      onRagSelect(ragId)
    }
    setSelectedRags(newSelected)
  }

  // Função para obter nome limpo do documento
  const getDocumentDisplayName = (doc: RAGDocument): string => {
    // Se tiver sourceFileName, usa ele
    if (doc.sourceFileName) {
      return doc.sourceFileName.replace(/\.[^/.]+$/, '') // Remove extensão
    }
    // Senão, usa o title mas limita a 50 caracteres
    const cleanTitle = doc.title.length > 50 ? doc.title.substring(0, 50) + '...' : doc.title
    return cleanTitle
  }
  
  // Handler para toggle de ferramenta
  const handleToolToggle = async (toolId: string, enabled: boolean) => {
    if (!selectedAgentId) return
    
    try {
      const endpoint = enabled 
        ? API_ENDPOINTS.AGENT_TOOL_ENABLE(selectedAgentId, toolId)
        : API_ENDPOINTS.AGENT_TOOL_DISABLE(selectedAgentId, toolId)
      
      const response = await authenticatedFetch(endpoint, {
        method: 'POST'
      })
      
      if (response.ok) {
        // Recarregar ferramentas do agente
        const agentToolsResponse = await authenticatedFetch(API_ENDPOINTS.AGENT_TOOLS(selectedAgentId))
        if (agentToolsResponse.ok) {
          const agentToolsData = await agentToolsResponse.json()
          setAgentTools(agentToolsData.data || [])
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar ferramenta:', error)
    }
  }
  


  // Verificar se ferramenta está habilitada (memoizado para evitar re-renders)
  const enabledToolsMap = useMemo(() => {
    const map = new Map<string, boolean>()
    agentTools.forEach(tool => {
      map.set(tool.id, tool.is_enabled === 1 || tool.is_enabled === true)
    })
    return map
  }, [agentTools])

  const isToolEnabled = (toolId: string) => {
    return enabledToolsMap.get(toolId) || false
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center glass-overlay">
      {/* Notificação Toast */}
      {notification && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[100] px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-in slide-in-from-top-2 fade-in duration-300 ${
            notification.type === 'success'
              ? 'bg-green-500/90 text-white border border-green-400'
              : 'bg-red-500/90 text-white border border-red-400'
          }`}
        >
          {notification.type === 'success' ? (
            <FiCheck className="w-5 h-5" />
          ) : (
            <FiX className="w-5 h-5" />
          )}
          <span className="font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-2 p-1 hover:bg-white/20 rounded transition-colors"
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="glass-panel rounded-lg w-[90vw] h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* Dropdown de Agentes */}
            <div className="relative">
              {isEditingAgentName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedAgentName}
                    onChange={(e) => setEditedAgentName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveAgentName()
                      if (e.key === 'Escape') setIsEditingAgentName(false)
                    }}
                    className="px-3 py-1.5 bg-background border border-primary rounded-lg text-lg font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveAgentName}
                    className="p-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                    title="Salvar"
                  >
                    <FiCheck className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary rounded-lg transition-colors group"
                >
                  {currentAgent ? (
                    <>
                      {/* Ícone clicável */}
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsEmojiPickerOpen(!isEmojiPickerOpen)
                          }}
                          className="p-2 hover:bg-secondary rounded-lg transition-all hover:scale-110"
                          title="Alterar ícone"
                        >
                          {renderAgentIcon(currentAgent.emoji)}
                        </button>
                        
                        {/* Icon Picker */}
                        {isEmojiPickerOpen && (
                          <div className="absolute top-full left-0 mt-2 bg-card border border-border rounded-lg shadow-xl p-3 grid grid-cols-5 gap-2 w-[240px] z-[60]" onClick={(e) => e.stopPropagation()}>
                            {iconOptions.map((option) => (
                              <button
                                key={option.name}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleUpdateEmoji(option.name)
                                }}
                                className="p-2 hover:bg-secondary rounded-lg transition-colors flex items-center justify-center"
                                title={option.name}
                              >
                                {option.icon}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <h2 className="text-xl font-semibold text-card-foreground">{currentAgent.name}</h2>
                    </>
                  ) : (
                    <h2 className="text-xl font-semibold text-muted-foreground">Selecione um agente</h2>
                  )}
                  <FiChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              )}

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                  {agents.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      Nenhum agente criado ainda
                    </div>
                  ) : (
                    agents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => handleSelectAgent(agent.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors text-left border-b border-border last:border-b-0 ${
                          selectedAgentId === agent.id ? 'bg-primary/10' : ''
                        }`}
                      >
                        <div className="flex items-center justify-center w-8 h-8">
                          {renderAgentIcon(agent.emoji)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{agent.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{agent.description}</div>
                        </div>
                        {agent.isDefault && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Padrão</span>
                        )}
                      </button>
                    ))
                  )}
                  
                  {/* Criar Novo Agente */}
                  <div className="border-t border-border">
                    {isCreatingAgent ? (
                      <div className="p-3 space-y-2">
                        {/* Seletor de Ícone */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Ícone:</span>
                          <div className="relative">
                            <button
                              onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
                              className="p-2 hover:bg-secondary rounded-lg transition-all hover:scale-110"
                              type="button"
                            >
                              {renderAgentIcon(newAgentEmoji)}
                            </button>
                            {isEmojiPickerOpen && (
                              <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-xl p-3 grid grid-cols-5 gap-2 w-[240px] z-[60]">
                                {iconOptions.map((option) => (
                                  <button
                                    key={option.name}
                                    onClick={() => {
                                      setNewAgentEmoji(option.name)
                                      setIsEmojiPickerOpen(false)
                                    }}
                                    className="p-2 hover:bg-secondary rounded-lg transition-colors flex items-center justify-center"
                                    type="button"
                                    title={option.name}
                                  >
                                    {option.icon}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <input
                          type="text"
                          value={newAgentName}
                          onChange={(e) => setNewAgentName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateAgent()
                            if (e.key === 'Escape') setIsCreatingAgent(false)
                          }}
                          placeholder="Nome do novo agente"
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleCreateAgent}
                            className="flex-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
                          >
                            Criar
                          </button>
                          <button
                            onClick={() => setIsCreatingAgent(false)}
                            className="px-3 py-1.5 bg-secondary rounded-lg text-sm hover:bg-secondary/80"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsCreatingAgent(true)}
                        className="w-full px-4 py-3 text-left hover:bg-secondary transition-colors text-primary font-medium text-sm"
                      >
                        + Criar Novo Agente
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Botão de editar nome (só aparece quando tem agente selecionado) */}
            {currentAgent && !isEditingAgentName && (
              <>
                <button
                  onClick={() => setIsEditingAgentName(true)}
                  className="p-2 hover:bg-secondary rounded-lg transition-colors"
                  title="Editar nome do agente"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
                
                {/* Botão de compartilhar/gerenciar link */}
                <button
                  onClick={() => shareUrl ? setShowShareModal(true) : handleEnableSharing()}
                  disabled={isEnablingShare}
                  className={`p-2 hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50 ${
                    shareEnabled ? 'text-green-600' : 'text-primary'
                  }`}
                  title={shareUrl ? 'Gerenciar link compartilhável' : 'Criar link compartilhável'}
                >
                  <FiShare2 className="w-4 h-4" />
                </button>
                
                {/* Botão de deletar agente */}
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                  title="Excluir agente permanentemente"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Botão Salvar */}
            <button
              onClick={async () => {
                if (selectedAgentId && onAgentUpdateFull) {
                  try {
                    await onAgentUpdateFull(selectedAgentId, { systemPrompt: prompt })
                    setNotification({ type: 'success', message: 'Prompt salvo com sucesso!' })
                  } catch (error) {
                    console.error('Erro ao salvar prompt:', error)
                    setNotification({ type: 'error', message: 'Erro ao salvar prompt' })
                  }
                }
              }}
              title="Salvar Prompt"
              className="p-2 hover:bg-secondary rounded-lg transition-colors border border-border"
            >
              <FiSave className="w-4 h-4 text-foreground" />
            </button>
            {/* Botão Otimizar */}
            <button
              onClick={() => !isOptimizing && onOptimizePrompt(prompt)}
              disabled={isOptimizing}
              title="Otimizar Prompt"
              className="p-2 hover:bg-secondary rounded-lg transition-colors disabled:opacity-50 border border-border"
            >
              <FiZap className="w-4 h-4 text-foreground" />
            </button>
            {/* Botão Fechar */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-lg transition-colors border border-border"
              title="Fechar"
            >
              <FiX className="w-5 h-5 text-foreground" />
            </button>
          </div>
        </div>

        {/* Content: 50% / 50% Layout com Painéis Dragáveis */}
        <div className="flex-1 flex overflow-hidden">
          {/* Coluna 50% Esquerda - Prompt Principal */}
          <div className="w-[50%] p-6 border-r border-border overflow-y-auto hide-scrollbar relative bg-background">
            <div className="mb-3">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                <FiMessageSquare className="w-3.5 h-3.5 text-muted-foreground" />
                Prompt Principal do Agente
              </label>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Digite o prompt do seu agente aqui...

Exemplo:
Você é um assistente prestativo e profissional..."
              className="w-full h-[calc(100%-40px)] resize-none bg-background border border-border rounded-md p-4 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-sm leading-relaxed"
            />
          </div>

          {/* Coluna 50% Direita - Painéis Colapsáveis */}
          <div className="w-[50%] flex flex-col gap-3 p-4 overflow-y-auto hide-scrollbar bg-background">
            {/* Painel 1: Prompt de Ferramentas */}
            <CollapsiblePanel
              id="tools-prompt"
              title="Prompt de Ferramentas"
              icon={<FiTool className="w-5 h-5" />}
              isExpanded={expandedPanel === 'tools-prompt'}
              onToggle={(id) => setExpandedPanel(expandedPanel === id ? '' : id)}
            >
              <div className="p-4">
                <div className="flex justify-end mb-3">
                  <button
                    onClick={async () => {
                      if (isEditingToolsPrompt && selectedAgentId && onAgentUpdateFull) {
                        try {
                          await onAgentUpdateFull(selectedAgentId, { toolsPrompt })
                          console.log('Prompt de ferramentas salvo')
                        } catch (error) {
                          console.error('Erro ao salvar prompt de ferramentas:', error)
                        }
                      }
                      setIsEditingToolsPrompt(!isEditingToolsPrompt)
                    }}
                    className="text-xs px-2 py-1 rounded border border-border bg-background text-foreground hover:bg-secondary transition-colors flex items-center gap-1"
                  >
                    {isEditingToolsPrompt ? (
                      <>
                        <FiSave className="w-3 h-3" />
                        Salvar
                      </>
                    ) : (
                      <>
                        <FiEdit className="w-3 h-3" />
                        Editar
                      </>
                    )}
                  </button>
                </div>
                {isEditingToolsPrompt ? (
                  <textarea
                    value={toolsPrompt}
                    onChange={(e) => setToolsPrompt(e.target.value)}
                    className="w-full h-[300px] resize-none bg-background border border-border rounded-md p-3 text-xs text-foreground font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Digite o prompt de ferramentas..."
                  />
                ) : (
                  <div className="bg-secondary/30 p-3 rounded-md border border-border max-h-[400px] overflow-y-auto hide-scrollbar">
                    <pre className="font-mono text-[10px] leading-relaxed text-foreground/80 whitespace-pre-wrap">
                      {toolsPrompt}
                    </pre>
                  </div>
                )}
              </div>
            </CollapsiblePanel>

            {/* Painel 2: Ferramentas */}
            <CollapsiblePanel
              id="tools"
              title="Ferramentas"
              icon={<FiSettings className="w-5 h-5" />}
              isExpanded={expandedPanel === 'tools'}
              onToggle={(id) => setExpandedPanel(expandedPanel === id ? '' : id)}
            >
              <div className="p-4">
                {/* Modelo LLM */}
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    Modelo LLM
                  </h4>
                  <div className="relative">
                    <select
                      value={llmConfig.model}
                      onChange={async (e) => {
                        const newModel = e.target.value
                        setLLMConfig({ ...llmConfig, model: newModel })
                        
                        if (selectedAgentId && onAgentUpdateFull) {
                          try {
                            await onAgentUpdateFull(selectedAgentId, { model: newModel })
                            console.log('[LLM] Modelo salvo:', newModel)
                          } catch (error) {
                            console.error('[LLM] Erro ao salvar modelo:', error)
                          }
                        }
                      }}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground appearance-none cursor-pointer hover:bg-secondary/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                    >
                      <optgroup label="Google Gemini">
                        <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                        <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                        <option value="gemini-2.0-flash-exp">Gemini 2.0 Flash Exp</option>
                        <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                        <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                      </optgroup>
                      <optgroup label="OpenAI">
                        <option value="gpt-4o">GPT-4o</option>
                        <option value="gpt-4o-mini">GPT-4o Mini</option>
                      </optgroup>
                    </select>
                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>

                {/* Lista de Ferramentas */}
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    Ferramentas Disponíveis
                  </h4>
                  {isLoadingTools ? (
                    <p className="text-xs text-muted-foreground italic">Carregando...</p>
                  ) : availableTools.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">Nenhuma ferramenta disponível</p>
                  ) : (
                    <div className="space-y-3">
                      {availableTools.map((tool) => {
                        const enabled = isToolEnabled(tool.id)
                        return (
                          <div key={tool.id} className="border border-border rounded-lg p-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={enabled}
                                onChange={(e) => {
                                  console.log('[TOOL TOGGLE]', tool.name, e.target.checked)
                                  handleToolToggle(tool.id, e.target.checked)
                                }}
                                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50 cursor-pointer flex-shrink-0"
                              />
                              {tool.name === 'image_generation' ? (
                                <FiTrendingUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              ) : (
                                <FiTool className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium flex-1">{tool.displayName || tool.display_name}</span>
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </CollapsiblePanel>

            {/* Painel 3: Base de Conhecimento */}
            <CollapsiblePanel
              id="knowledge"
              title="Base de Conhecimento"
              icon={<FiDatabase className="w-5 h-5" />}
              isExpanded={expandedPanel === 'knowledge'}
              onToggle={(id) => setExpandedPanel(expandedPanel === id ? '' : id)}
            >
              <div className="p-4">
                {ragDocuments.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Nenhum documento disponível</p>
                ) : (
                  <div className="space-y-0">
                    {ragDocuments.map((doc, index) => (
                      <div key={doc.id}>
                        <label className="flex items-center gap-2 px-2 py-2 rounded hover:bg-secondary/40 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedRags.has(doc.id)}
                            onChange={() => handleRagToggle(doc.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/50 cursor-pointer flex-shrink-0"
                          />
                          <span className="text-sm truncate flex-1 font-medium" title={doc.sourceFileName || doc.title}>
                            {getDocumentDisplayName(doc)}
                          </span>
                          {doc.chunks && <span className="text-[10px] text-muted-foreground px-1.5 py-0.5 bg-muted rounded flex-shrink-0">{doc.chunks.length}</span>}
                        </label>
                        {index < ragDocuments.length - 1 && (
                          <div className="h-px bg-border mx-2"></div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CollapsiblePanel>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center glass-overlay">
          <div className="glass-panel border border-destructive rounded-lg w-[450px] p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-destructive/10 rounded-full">
                <FiTrash2 className="w-6 h-6 text-destructive" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-card-foreground mb-2">
                  Excluir Agente Permanentemente
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Tem certeza que deseja excluir <strong>{currentAgent?.name}</strong>? 
                  Esta ação é irreversível e irá deletar:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 mb-6">
                  <li>Todas as conversas do agente</li>
                  <li>Todas as mensagens e histórico</li>
                  <li>Todas as configurações personalizadas</li>
                  <li>Estatísticas e dados de uso</li>
                </ul>
                <div className="flex gap-3">
                  <button
                    onClick={handleDeleteAgent}
                    className="flex-1 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 font-medium transition-colors"
                  >
                    Sim, Excluir Permanentemente
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Compartilhamento */}
      {showShareModal && shareUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center glass-overlay">
          <div className="glass-panel border border-primary rounded-lg w-[550px] p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <FiShare2 className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FiShare2 className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-semibold text-card-foreground">
                    Link Compartilhável Criado
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Compartilhe este link com seus clientes para que eles possam testar o agente <strong>{currentAgent?.name}</strong>
                </p>
                
                {/* Toggle Ativar/Desativar */}
                <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">Status do Link</p>
                      <p className="text-xs text-muted-foreground">
                        {shareEnabled ? 'Link ativo e acessível' : 'Link desativado temporariamente'}
                      </p>
                    </div>
                    <button
                      onClick={handleToggleSharing}
                      disabled={isTogglingShare}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                        shareEnabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          shareEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
                
                {/* URL Box */}
                <div className="mb-4 p-3 bg-secondary rounded-lg border border-border">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 bg-transparent text-sm text-foreground outline-none"
                    />
                    <button
                      onClick={handleCopyUrl}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-background rounded-lg transition-colors"
                      title="Copiar URL"
                    >
                      <FiCopy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FiBook className="w-4 h-4 text-muted-foreground" />
                    <p className="text-xs font-semibold text-foreground">Como funciona:</p>
                  </div>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1 mt-2">
                    <li>Clientes podem conversar com o agente sem login</li>
                    <li>Todas as conversas são salvas no seu banco de dados</li>
                    <li>Você pode acompanhar todas as interações em tempo real</li>
                    <li>O agente usa o mesmo prompt, RAGs e ferramentas configurados</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors text-center ${
                      shareEnabled 
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    onClick={(e) => !shareEnabled && e.preventDefault()}
                  >
                    Abrir Link
                  </a>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="px-4 py-2 bg-secondary rounded-lg hover:bg-secondary/80 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
